/**
 * pedagogieCommune.js — Cadre pédagogique commun aux 3 trames
 * (Plateau A1/A2, Formation 125, Passerelle A2 → A).
 *
 * Contenu 100% neutre : ne mentionne aucune école, aucune entreprise.
 * Utilisable par tout enseignant motocycliste ou tout élève 2-roues.
 */

export const METHODE_BASELINE = 'VOIR → COMPRENDRE → PRÉPARER → AGIR → CONTRÔLER → S’AUTO-CORRIGER';

export const CINQ_QUESTIONS = [
  { key: 'pourquoi',  label: 'POURQUOI ?',    text: "Quel est l'intérêt de cette compétence ?" },
  { key: 'comment',   label: 'COMMENT ?',     text: 'Quelle technique utilise-t-on ?' },
  { key: 'risques',   label: 'RISQUES ?',     text: 'Que peut-il se passer si la technique est mal maîtrisée ?' },
  { key: 'influences',label: 'INFLUENCES ?',  text: "Qu'est-ce qui peut modifier le comportement de la moto ou du conducteur ?" },
  { key: 'auto',      label: 'AUTO-ÉVAL.',    text: "Comment je sais que j'ai réussi ?" },
];

/**
 * 6 niveaux de progression réutilisables pour classer les variantes
 * d'un même exercice, du plus guidé au plus autonome.
 */
export const NIVEAUX_PROGRESSION = [
  { n: 1, code: 'Découverte',    desc: "L'enseignant explique et démontre."                              , color: '#22c55e' },
  { n: 2, code: 'Reproduction',  desc: "L'élève reproduit la technique avec un guidage rapproché."       , color: '#84cc16' },
  { n: 3, code: 'Stabilisation', desc: "L'élève répète avec moins d'intervention de l'enseignant."       , color: '#f59e0b' },
  { n: 4, code: 'Adaptation',    desc: 'On modifie vitesse, rayon, trajectoire ou environnement.'        , color: '#f97316' },
  { n: 5, code: 'Enchaînement',  desc: "Plusieurs compétences sont associées dans une même situation."   , color: '#ef4444' },
  { n: 6, code: 'Autonomie',     desc: "L'élève choisit ses actions et analyse lui-même son résultat."   , color: '#a855f7' },
];

/**
 * Principe de remédiation : si une compétence échoue, on ne répète pas
 * mécaniquement — on cherche la CAUSE puis on construit un exercice
 * intermédiaire qui isole la difficulté.
 */
export const PRINCIPE_REMEDIATION = {
  regleOr: 'Un exercice n\u2019est jamais une fin en soi. Il est choisi pour faire acquérir, renforcer ou transférer une compétence. L\u2019enseignant adapte la difficulté aux capacités de l\u2019élève, observe ses réussites et ses difficultés, puis construit la situation suivante en conséquence.',
  demarche: [
    { etape: 'Observer',    detail: "Identifier ce qui se passe (regard, embrayage, frein arrière, position, crispation, allure)." },
    { etape: 'Chercher',    detail: 'Rechercher la cause plutôt que le symptôme (pourquoi l\u2019élève a-t-il posé le pied ?).' },
    { etape: 'Isoler',      detail: "Créer un exercice intermédiaire qui isole la difficulté (ex. patinage + frein arrière en ligne droite)." },
    { etape: 'Complexifier',detail: 'Augmenter progressivement : ligne droite → courbe large → huit → demi-tour.' },
    { etape: 'Réintégrer',  detail: 'Réintégrer la compétence dans le parcours complet.' },
    { etape: 'Débriefer',   detail: "Faire verbaliser l'élève : «Qu'est-ce qui a changé ? Pourquoi ?»" },
  ],
};

/**
 * Structure normalisée d'une fiche pédagogique — chaque compétence
 * suit cette grille pour rester exploitable par tout enseignant.
 */
export const FICHE_TEMPLATE_LABELS = {
  objectif:    { label: 'Objectif',                  icon: 'Target',       tone: 'emerald' },
  pourquoi:    { label: 'Pourquoi ?',                icon: 'HelpCircle',   tone: 'sky' },
  comment:     { label: 'Comment ?',                 icon: 'GraduationCap',tone: 'sky' },
  exercices:   { label: 'Exercices',                 icon: 'Bike',         tone: 'white' },
  observation: { label: 'Ce que l’enseignant observe', icon: 'Eye',        tone: 'amber' },
  risques:     { label: 'Risques',                   icon: 'AlertTriangle',tone: 'rose' },
  influences:  { label: 'Influences',                icon: 'Wind',         tone: 'amber' },
  erreurs:     { label: 'Erreurs fréquentes',        icon: 'X',            tone: 'rose' },
  remediation: { label: 'Remédiation',               icon: 'Wrench',       tone: 'sky' },
  questions:   { label: 'Questions pédagogiques',    icon: 'MessageCircle',tone: 'rose' },
  critere:     { label: 'Critère de réussite',       icon: 'CheckCircle2', tone: 'emerald' },
  aRetenir:    { label: 'À retenir',                 icon: 'Sparkles',     tone: 'emerald' },
};

/**
 * Phrase d'ouverture recommandée pour introduire le référentiel.
 */
export const OUVERTURE_REFERENTIEL = "Cette méthode pédagogique propose un cadre commun aux formations 2-roues (Plateau A1/A2, Formation 125, Passerelle A2 → A). Chaque compétence est présentée sous forme d'une fiche exploitable en salle, sur le plateau ou en circulation. La progression est adaptée au niveau réel de chaque élève : les exercices ne sont pas réalisés de manière systématique mais sélectionnés en fonction du niveau, de l'expérience et des besoins observés.";
